import React, { useState } from 'react';
import { DEVICE_SPECS } from '../../utils/deviceSpecs';
import useToolStore from '../../store/useToolStore';

const DeviceFrame = ({ children }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const selectedDevice = useToolStore((state) => state.selectedDevice);
  const device = DEVICE_SPECS[selectedDevice] || DEVICE_SPECS['iphone-15-pro'];
  
  // Visual scale multiplier to make device larger and more visible
  // This increases visual size while maintaining exact resolution (width x height in logical points)
  const visualScaleMultiplier = 1.8;
  const visualScale = device.scale * visualScaleMultiplier;
  
  const bezelWidth = device.bezelWidth || 8;
  const scaledBezelWidth = bezelWidth * visualScaleMultiplier;
  const deviceWidth = device.width * visualScale;
  const deviceHeight = device.height * visualScale;
  const screenWidth = deviceWidth - (scaledBezelWidth * 2);
  const screenHeight = deviceHeight - (scaledBezelWidth * 2);
  const cornerRadius = (device.cornerRadius || 47) * visualScale;
  
  // Bezel colors based on device type
  const getBezelGradient = () => {
    if (device.bezelColor === 'titanium') {
      // Titanium finish for Pro models
      return 'linear-gradient(135deg, #8e8e93 0%, #636366 25%, #48484a 50%, #636366 75%, #8e8e93 100%)';
    } else {
      // Aluminum finish for regular models
      return 'linear-gradient(135deg, #d1d1d6 0%, #a1a1a6 25%, #8e8e93 50%, #a1a1a6 75%, #d1d1d6 100%)';
    }
  };

  // Calculate notch/Dynamic Island positions (scaled)
  const notchTop = scaledBezelWidth + (2 * visualScaleMultiplier);
  const dynamicIslandTop = scaledBezelWidth + (4 * visualScaleMultiplier);
  const homeIndicatorBottom = scaledBezelWidth + (8 * visualScaleMultiplier);

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <div className="flex flex-col items-center justify-center w-full h-full p-4 pt-2 relative">
      {/* Minimize/Maximize Button */}
      <button
        onClick={toggleMinimize}
        className="absolute top-2 right-2 z-30 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-200 dark:border-gray-700"
        title={isMinimized ? 'Maximize' : 'Minimize'}
      >
        {isMinimized ? (
          <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
          </svg>
        )}
      </button>
      <div
        className={`relative flex-shrink-0 transition-all duration-300 ${isMinimized ? 'scale-50 opacity-50' : 'scale-100 opacity-100'}`}
        style={{
          width: `${deviceWidth}px`,
          height: `${deviceHeight}px`,
        }}
      >
        {/* Outer Bezel Shadow */}
        <div
          className="absolute inset-0 rounded-[3rem]"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(0,0,0,0.3) 0%, transparent 70%)',
            borderRadius: `${cornerRadius + bezelWidth}px`,
            zIndex: 0,
          }}
        />
        
        {/* Device Bezel */}
        <div
          className="relative rounded-[3rem]"
          style={{
            width: `${deviceWidth}px`,
            height: `${deviceHeight}px`,
            background: getBezelGradient(),
            borderRadius: `${cornerRadius + scaledBezelWidth}px`,
            padding: `${scaledBezelWidth}px`,
            boxShadow: `
              0 0 0 1px rgba(0, 0, 0, 0.1) inset,
              0 0 0 1px rgba(255, 255, 255, 0.05),
              0 20px 60px rgba(0, 0, 0, 0.4),
              0 0 100px rgba(0, 0, 0, 0.2)
            `,
            zIndex: 1,
          }}
        >
          {/* Notch */}
          {device.notch && (
            <div
              className="absolute bg-black rounded-b-[20px] z-20"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${notchTop}px`,
                width: `${(device.notchWidth || 126) * visualScale}px`,
                height: `${(device.notchHeight || 30) * visualScale}px`,
                borderRadius: `0 0 ${20 * visualScale}px ${20 * visualScale}px`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}
            >
              {/* Notch speaker */}
              <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 rounded-full"
                style={{
                  width: `${40 * visualScale}px`,
                  height: `${6 * visualScale}px`,
                }}
              />
              {/* Notch camera */}
              <div
                className="absolute top-1/2 right-2 bg-gray-900 rounded-full"
                style={{
                  width: `${8 * visualScale}px`,
                  height: `${8 * visualScale}px`,
                  transform: 'translateY(-50%)',
                }}
              />
            </div>
          )}
          
          {/* Dynamic Island */}
          {device.dynamicIsland && (
            <div
              className="absolute bg-black rounded-full z-20"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${dynamicIslandTop}px`,
                width: `${(device.dynamicIslandWidth || 126) * visualScale}px`,
                height: `${(device.dynamicIslandHeight || 37) * visualScale}px`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              }}
            >
              {/* Dynamic Island inner details */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
                }}
              />
            </div>
          )}
          
          {/* Screen */}
          <div
            className="relative bg-black overflow-hidden flex items-center justify-center"
            style={{
              width: `${screenWidth}px`,
              height: `${screenHeight}px`,
              borderRadius: `${cornerRadius}px`,
              boxShadow: `
                inset 0 0 0 1px rgba(0, 0, 0, 0.5),
                inset 0 0 20px rgba(0, 0, 0, 0.3)
              `,
            }}
          >
            {/* Screen Content Container - scaled to fit device dimensions */}
            {/* Resolution (device.width x device.height) remains unchanged - only visual scale increases */}
            <div
              className="bg-white"
              style={{
                transform: `scale(${visualScale})`,
                transformOrigin: 'center center',
                width: `${device.width}px`,
                height: `${device.height}px`,
                minHeight: `${device.height}px`,
              }}
            >
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{
                  width: `${device.width}px`,
                  height: `${device.height}px`,
                  padding: '16px',
                  boxSizing: 'border-box',
                  overflow: 'visible',
                }}
              >
                <div className="w-full h-full flex items-center justify-center" style={{ minWidth: 0, minHeight: 0 }}>
                  {children}
                </div>
              </div>
            </div>
            
            {/* Screen reflection overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.1) 100%)',
              }}
            />
          </div>
          
          {/* Home Indicator */}
          <div
            className="absolute left-1/2 transform -translate-x-1/2 bg-white rounded-full z-20"
            style={{
              bottom: `${homeIndicatorBottom}px`,
              width: `${(device.homeIndicatorWidth || 134) * visualScale}px`,
              height: `${(device.homeIndicatorHeight || 5) * visualScale}px`,
              opacity: 0.6,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceFrame;
